import { supabase } from '../../supabase';

// Shared user cache for reducing redundant author fetches across modules
const userCache = new Map<string, any>();

/**
 * Fetch user data for given IDs, using cache to avoid redundant queries.
 * Returns a Map of userId -> user data.
 */
export async function fetchUsersWithCache(
  authorIds: string[],
  signal?: AbortSignal
): Promise<Map<string, any>> {
  // Early abort check
  if (signal?.aborted) {
    throw new DOMException('Request was aborted', 'AbortError');
  }

  const uncachedIds = authorIds.filter(id => !userCache.has(id));

  if (uncachedIds.length > 0) {
    try {
      const query = supabase
        .from('users')
        .select('id,name,role,institution')
        .in('id', uncachedIds);

      const { data: users, error } = signal
        ? await query.abortSignal(signal)
        : await query;

      if (error) {
        if (signal?.aborted) {
          throw new DOMException('Request was aborted', 'AbortError');
        }
        // Silently skip on non-abort errors (cache miss is acceptable)
      }

      if (users) {
        users.forEach(user => userCache.set(user.id, user));
      }
    } catch (e: any) {
      if (signal?.aborted || (e && typeof e === 'object' && 'name' in e && e.name === 'AbortError')) {
        throw new DOMException('Request was aborted', 'AbortError');
      }
      // Silently skip on other errors — cached results still returned
    }
  }

  const result = new Map<string, any>();
  authorIds.forEach(id => {
    if (userCache.has(id)) {
      result.set(id, userCache.get(id));
    }
  });
  return result;
}

export { userCache };
